import { useCallback, useEffect, useState } from 'react';

import { ChevronLeftIcon, CloseIcon } from '@braskai/icons';
import { Button, IconButton, Modal, ModalActions, ModalContent, ModalWrapper, Typography } from '@braskai/ui-kit';

import { CircularProgress, Stack } from '@mui/material';

import { usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';

import { IBoostersInvoicePreviewResponse } from '@/shared/api/billing/types';
import { FeaturedIconShield } from '@/shared/assets/icons/FeaturedIconShield';
import { useCustomerPortal } from '@/shared/hooks/useCustomerPortal';

import { FuturePaymentCard } from './components/FuturePaymentCard';
import { InvoiceLineItem } from './components/InvoiceLineItem';
import { NoPaymentDueCard } from './components/NoPaymentDueCard';
import { PaymentMethodCard } from './components/PaymentMethodCard';
import { PromoCodeInput } from './components/PromoCodeInput';
import { PromoCodeLine } from './components/PromoCodeLine';
import { TypographyLink } from './components/TypographyLink';
import { VATLine } from './components/VATLine';
import { LINKS } from './constants';
import { usePriceFormatting } from './hooks/usePriceFormatting';

const hasDiscountApplied = (invoice: IBoostersInvoicePreviewResponse | null): boolean => {
  if (!invoice) return false;

  const instantItems = invoice.instant_payment?.items ?? [];
  const futureItems = invoice.future_payments?.flatMap((p) => p.items) ?? [];
  const allItems = [...instantItems, ...futureItems];

  return allItems.some((item) => item.discount_amount > 0);
};

const calculateTotalDiscount = (invoice: IBoostersInvoicePreviewResponse | null): number => {
  if (!invoice) return 0;

  const instantItems = invoice.instant_payment?.items ?? [];

  return instantItems.reduce((total, item) => total + (item.discount_amount || 0), 0);
};

const calculateDiscountPercentage = (invoice: IBoostersInvoicePreviewResponse | null): null | number => {
  if (!invoice) return null;

  const futureItems = invoice.future_payments?.[0]?.items ?? [];
  const itemWithDiscount = futureItems.find((item) => item.discount_amount > 0);

  if (!itemWithDiscount || !itemWithDiscount.amount) return null;

  const { amount, discount_amount } = itemWithDiscount;
  const percentage = Math.round((discount_amount / amount) * 100);

  return percentage;
};

const getPromoHint = (invoice: IBoostersInvoicePreviewResponse | null): string | undefined => {
  const percentage = calculateDiscountPercentage(invoice);

  if (!percentage) return undefined;

  return `Discount ${percentage}% applies to the first invoice only.`;
};

const PurchaseConfirmationModalComponent = () => {
  const fetchPreview = usePurchaseConfirmationModalStore((state) => state.fetchPreview);
  const onConfirm = usePurchaseConfirmationModalStore((state) => state.onConfirm);
  const renderExtra = usePurchaseConfirmationModalStore((state) => state.renderExtra);
  const confirmButtonText = usePurchaseConfirmationModalStore((state) => state.confirmButtonText);
  const onConfirmAnalytics = usePurchaseConfirmationModalStore((state) => state.onConfirmAnalytics);
  const close = usePurchaseConfirmationModalStore((state) => state.close);
  const onCancel = usePurchaseConfirmationModalStore((state) => state.onCancel);
  const promoCodeConfig = usePurchaseConfirmationModalStore((state) => state.promoCodeConfig);

  const { calculateVATPercentage, formatPrice } = usePriceFormatting();
  const { openCustomerPortal } = useCustomerPortal();

  const [invoice, setInvoice] = useState<IBoostersInvoicePreviewResponse | null>(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Promo code state
  const [appliedPromoCode, setAppliedPromoCode] = useState<null | string>(null);
  const [promoError, setPromoError] = useState<null | string>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const [isPromoCodeShown, setIsPromoCodeShown] = useState(false);

  const loadPreview = useCallback(
    async (promoCode?: null | string, showFullLoader = true) => {
      if (!fetchPreview) return null;

      if (showFullLoader) {
        setIsFetchingPreview(true);
      }
      try {
        const res = await fetchPreview(promoCode);
        setInvoice(res);
        return res;
      } catch (err) {
        console.error('Failed to fetch invoice preview:', err);
        return null;
      } finally {
        if (showFullLoader) {
          setIsFetchingPreview(false);
        }
      }
    },
    [fetchPreview],
  );

  useEffect(() => {
    void loadPreview(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyPromo = useCallback(
    async (code: string) => {
      setIsApplyingPromo(true);
      setPromoError(null);

      try {
        // Don't show full loader - keep preview visible, only show spinner in input
        const res = await loadPreview(code, false);

        if (hasDiscountApplied(res)) {
          setAppliedPromoCode(code);
        } else {
          setPromoError('The promo code is invalid');
          setAppliedPromoCode(null);
          // Reload preview without the invalid code
          await loadPreview(null, false);
        }
      } catch {
        setPromoError('Failed to apply promo code');
        setAppliedPromoCode(null);
      } finally {
        setIsApplyingPromo(false);
      }
    },
    [loadPreview],
  );

  const handleRemovePromo = useCallback(async () => {
    setAppliedPromoCode(null);
    setPromoError(null);
    // Reload preview without promo code, don't show full loader
    await loadPreview(null, false);
  }, [loadPreview]);

  const handleConfirm = useCallback(async () => {
    if (!invoice || !onConfirm) return;

    onConfirmAnalytics?.();
    setIsConfirming(true);
    try {
      await onConfirm(invoice, appliedPromoCode);
    } catch (err) {
      console.error('Purchase confirmation failed:', err);
    } finally {
      setIsConfirming(false);
      close();
    }
  }, [invoice, onConfirm, close, appliedPromoCode, onConfirmAnalytics]);

  const handleEditPaymentMethod = () => {
    void openCustomerPortal({ navigateTo: 'portal' });
  };

  const formatPaymentMethod = () => {
    if (!invoice?.payment_method?.details) return undefined;

    const { last4 } = invoice.payment_method.details;

    return `****${last4}`;
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    close();
  };

  const getConfirmButtonText = () => {
    if (confirmButtonText) return confirmButtonText;
    return invoice?.instant_payment ? 'Subscribe with obligation to pay' : 'Confirm';
  };

  return (
    <Modal size="xs" open onClose={handleCancel}>
      <ModalContent>
        {/* Header */}
        <Stack alignItems="flex-start" flexDirection="row" justifyContent="space-between">
          <FeaturedIconShield height={56} width={56} />
          <IconButton disableFixSizeIcon onClick={close}>
            <CloseIcon height={24} width={24} />
          </IconButton>
        </Stack>

        {isFetchingPreview ? (
          <Stack alignItems="center" height={300} justifyContent="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack gap={20} mt={16}>
            {/* Amount Due Today Section */}
            <Stack>
              <Stack gap={4}>
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Typography color="grey.900" variant="title-4-md">
                    Amount due today
                  </Typography>
                  <Typography color="grey.900" variant="title-4-md">
                    {invoice?.instant_payment
                      ? formatPrice(
                          invoice.instant_payment.amount + (invoice.instant_payment.tax_amount ?? 0),
                          invoice.instant_payment.currency,
                        )
                      : '$0.00'}
                  </Typography>
                </Stack>
                <Typography color="grey.500" variant="body-2">
                  Total includes taxes based on your billing info.
                  {invoice?.instant_payment?.amount && (
                    <>
                      <br />
                      One time payment covers usage till the end of billing cycle.
                    </>
                  )}
                </Typography>
              </Stack>

              {/* Extra content (e.g. removed boosters list, promo code field) */}
              {renderExtra?.()}

              {/* Line Items */}
              <Stack gap={4} marginTop={renderExtra ? 0 : 16}>
                {invoice?.instant_payment?.items.map((item) => (
                  <InvoiceLineItem
                    key={`${item.description}-${item.amount}`}
                    formatPrice={formatPrice}
                    item={item}
                    showRemoveLabel
                  />
                ))}
                {invoice?.instant_payment && (
                  <VATLine
                    calculateVATPercentage={calculateVATPercentage}
                    currency={invoice.instant_payment.currency}
                    formatPrice={formatPrice}
                    taxAmount={invoice.instant_payment.tax_amount}
                    totalAmount={invoice.instant_payment.amount}
                  />
                )}
                {appliedPromoCode && invoice?.instant_payment && (
                  <PromoCodeLine
                    code={appliedPromoCode}
                    currency={invoice.instant_payment.currency}
                    discountAmount={calculateTotalDiscount(invoice)}
                    formatPrice={formatPrice}
                  />
                )}
              </Stack>
            </Stack>

            {/* Future Charges */}
            {invoice?.future_payments?.map((payment) => (
              <FuturePaymentCard
                key={payment.date}
                calculateVATPercentage={calculateVATPercentage}
                formatPrice={formatPrice}
                interval={payment.interval}
                payment={payment}
                promoCode={appliedPromoCode}
              />
            ))}

            {/* Promo Code Input */}
            {promoCodeConfig?.enabled && isPromoCodeShown && (
              <PromoCodeInput
                appliedCode={appliedPromoCode ?? undefined}
                error={promoError ?? null}
                hint={appliedPromoCode ? getPromoHint(invoice) : undefined}
                isApplying={isApplyingPromo}
                onApply={handleApplyPromo}
                onRemove={handleRemovePromo}
              />
            )}

            {/* Promo Code Input */}
            {promoCodeConfig?.enabled && !isPromoCodeShown && (
              <Typography
                sx={{
                  cursor: 'pointer',
                }}
                color="primary.700"
                variant="caption-1-md"
                onClick={() => setIsPromoCodeShown(true)}
              >
                Have a promo code?
              </Typography>
            )}

            {/* Payment Method or No Payment Due */}
            {invoice?.instant_payment?.amount || invoice?.future_payments?.length ? (
              <PaymentMethodCard
                brand={invoice?.payment_method?.details?.brand}
                isError={!invoice.payment_method}
                paymentMethod={formatPaymentMethod()}
                typeName={invoice?.payment_method?.type_name}
                onEdit={handleEditPaymentMethod}
              />
            ) : (
              <NoPaymentDueCard />
            )}
          </Stack>
        )}
      </ModalContent>

      <ModalActions>
        <Stack gap={12} width="100%">
          <Stack direction="row" gap={12} width="100%">
            <Button
              sx={{
                minWidth: 44,
                padding: '12px',
              }}
              disabled={isFetchingPreview || isConfirming}
              size="lg"
              variant="outlined"
              onClick={handleCancel}
            >
              <Stack direction="row">
                <ChevronLeftIcon height={20} width={20} />
              </Stack>
            </Button>
            <Button
              sx={{
                flexGrow: 1,
              }}
              disabled={isFetchingPreview || isConfirming || !invoice?.payment_method}
              isLoading={isConfirming}
              size="lg"
              fullWidth
              onClick={handleConfirm}
            >
              {getConfirmButtonText()}
            </Button>
          </Stack>
          <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="caption-1">
            By confirming, you agree to Rask AI's{' '}
            <TypographyLink
              color="grey.700"
              href={LINKS.TERMS_OF_SERVICE}
              sx={{ textDecoration: 'none' }}
              target="_blank"
              variant="caption-1-md"
            >
              Terms of Service
            </TypographyLink>
            ,{' '}
            <TypographyLink
              color="grey.700"
              href={LINKS.PRIVACY_POLICY}
              sx={{ textDecoration: 'none' }}
              target="_blank"
              variant="caption-1-md"
            >
              Privacy Policy
            </TypographyLink>
            {promoCodeConfig?.enabled && (
              <>
                ,{' '}
                <TypographyLink
                  color="grey.700"
                  href={LINKS.PROMOTION_TERMS}
                  sx={{ textDecoration: 'none' }}
                  target="_blank"
                  variant="caption-1-md"
                >
                  Promotion Terms
                </TypographyLink>
              </>
            )}{' '}
            and{' '}
            <TypographyLink
              color="grey.700"
              href={LINKS.BOOSTERS_TERMS_OF_USE}
              sx={{ textDecoration: 'none' }}
              target="_blank"
              variant="caption-1-md"
            >
              Boosters Terms of Use
            </TypographyLink>
          </Typography>
        </Stack>
      </ModalActions>
    </Modal>
  );
};

export const PurchaseConfirmationModal = () => (
  <ModalWrapper hook={usePurchaseConfirmationModalStore}>
    <PurchaseConfirmationModalComponent />
  </ModalWrapper>
);
